import { useCallback, useRef, useState } from "react";

/**
 * useCardFlight (iter 76).
 *
 * Captures the rect of every hand-card and target zone (enemy / player /
 * battlefield) by ref, and returns a `flyCard(cardId, target, payload)`
 * trigger plus the JSX to render the in-flight projectile overlay.
 *
 * Usage in BattleArena:
 *   const { registerRef, flyCard, FlyOverlay } = useCardFlight();
 *   ...
 *   <BattleHand registerRef={registerRef} ... onPlayCard={(id) => {
 *     const card = battle.player_hand.find(c => c.id === id);
 *     const targetKey = card.type === "ally" ? "battlefield"
 *       : card.effect === "damage" ? "enemy" : "player";
 *     flyCard(id, targetKey, card);
 *     setTimeout(() => realPlayCard(id), 380);
 *   }} />
 *   <FlyOverlay />
 *
 * Animation: the card is cloned into a fixed-position absolute div, then
 * transformed from the source rect to the target rect over ~360ms with a
 * cubic-bezier easing, finishing in a flash + scale-up that quickly fades.
 */
export default function useCardFlight() {
  const refs = useRef({});
  const [flying, setFlying] = useState(null); // {id, payload, from, to, phase}

  const registerRef = useCallback((key, el) => {
    if (el) refs.current[key] = el;
    else delete refs.current[key];
  }, []);

  const flyCard = useCallback((cardId, targetKey, payload) => {
    const sourceEl = refs.current[cardId];
    const targetEl = refs.current[targetKey];
    if (!sourceEl || !targetEl) return;
    const from = sourceEl.getBoundingClientRect();
    const to = targetEl.getBoundingClientRect();
    setFlying({ id: cardId, payload, from, to, phase: "flying" });
    // Phase transition: after the move ends, flash impact then disappear
    setTimeout(() => setFlying((cur) => (cur && cur.id === cardId ? { ...cur, phase: "impact" } : cur)), 360);
    setTimeout(() => setFlying((cur) => (cur && cur.id === cardId ? null : cur)), 620);
  }, []);

  const FlyOverlay = useCallback(() => {
    if (!flying) return null;
    const { from, to, payload, phase } = flying;
    const initialStyle = {
      left: from.left,
      top: from.top,
      width: from.width,
      height: from.height,
    };
    const transform = phase === "flying"
      ? `translate(${to.left + to.width / 2 - from.left - from.width / 2}px, ${to.top + to.height / 2 - from.top - from.height / 2}px) scale(0.85) rotate(8deg)`
      : `translate(${to.left + to.width / 2 - from.left - from.width / 2}px, ${to.top + to.height / 2 - from.top - from.height / 2}px) scale(1.4) rotate(0deg)`;
    const opacity = phase === "impact" ? 0 : 1;

    // Tint the in-flight projectile by effect
    const tint = payload?.effect === "damage" ? "shadow-[0_0_40px_rgba(255,80,80,0.7)] ring-2 ring-red-400/70" :
      payload?.effect === "heal" ? "shadow-[0_0_40px_rgba(80,255,140,0.7)] ring-2 ring-emerald-400/70" :
      payload?.effect === "defense" ? "shadow-[0_0_40px_rgba(120,180,255,0.7)] ring-2 ring-blue-400/70" :
      payload?.type === "ally" ? "shadow-[0_0_40px_rgba(140,255,180,0.7)] ring-2 ring-emerald-300/80" :
      "shadow-[0_0_40px_rgba(207,181,59,0.7)] ring-2 ring-amber-300/70";

    return (
      <>
        <div
          className={`fixed z-[9999] pointer-events-none rounded-lg ${tint}`}
          style={{
            ...initialStyle,
            transform,
            opacity,
            transition: phase === "flying"
              ? "transform 360ms cubic-bezier(0.4, 1.4, 0.5, 1), opacity 200ms ease-out"
              : "transform 240ms ease-out, opacity 240ms ease-out",
            background: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(0,0,0,0.45))",
            backdropFilter: "blur(2px)",
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center text-center px-2">
            <span className="font-heading text-xs sm:text-sm text-white drop-shadow-lg leading-tight line-clamp-2">
              {payload?.name || ""}
            </span>
          </div>
        </div>
        {/* Impact flash at target */}
        {phase === "impact" && (
          <div
            className="fixed z-[9998] pointer-events-none rounded-full"
            style={{
              left: to.left + to.width / 2 - 50,
              top: to.top + to.height / 2 - 50,
              width: 100,
              height: 100,
              background: "radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,200,80,0.6) 30%, rgba(255,255,255,0) 70%)",
              animation: "ac-impact-pulse 240ms ease-out forwards",
            }}
          />
        )}
        <style>{`
          @keyframes ac-impact-pulse {
            0%   { transform: scale(0.4); opacity: 0; }
            40%  { transform: scale(1.5); opacity: 1; }
            100% { transform: scale(2.4); opacity: 0; }
          }
        `}</style>
      </>
    );
  }, [flying]);

  return { registerRef, flyCard, FlyOverlay };
}
